/**
 * @file 参考書ルートと学習者の設定から、科目ごとの学習スケジュールを組む純関数を置く。
 *
 * @what: 科目ごとに、使う本を選び (初学者 = 最下段から、学び直し = いまの偏差値より下で終わる本を
 *   飛ばす。目標より上で始まる本は使わない)、区分の進め方で列 (lane) に分ける。「順番」の区分は
 *   1本の列に連ね、「並行」の区分はそれぞれ1本の列にする。全部の列が同じ期間に収まるように、
 *   期間 (週の時間があればそこから、無ければ試験日から逆算) の中で本を量に比例して割り付ける。
 * @why: 単語帳と長文のように並行して進める区分を直列に並べると期間が見えなくなる (代表指摘
 *   2026-09-05)。週の時間が無くても試験日があれば「終えるには週 X 分」を出せる。
 *   スケジュールは保存せず毎回計算する (設計 §3.3)。量の無い本は「未登録」と出して合計に入れない。
 * @failure: 描画専用の純関数で、週の時間も試験日 (期限) も無い科目は列と本の並びだけを返し、日付は
 *   付けない。量の分かる本が1冊も無い科目も同じで、期間は決まらない。
 */

import type { StudyExamPlan, StudyExamSubjectSetting } from "../api/examPlans";
import type { StudyTextbookRouteBook, StudyTextbookRouteFlow } from "../api/textbookRoutes";
import {
  bookCeiling,
  bookDifficulty,
  type GridBook,
  type GridSubject,
  type GridTrack,
  type MergedTarget,
} from "./textbookRouteGrid";

/** 1単位にかける分の既定値 (出典に無いとき)。 */
export const DEFAULT_MINUTES_PER_UNIT: Readonly<Record<string, number>> = {
  問: 8,
  テーマ: 20,
  長文: 30,
  語: 0.3,
  文: 3,
  ページ: 10,
  章: 60,
};

/** 2周目以降は間違えた問題だけをやり直すとみなし、1周目の半分の時間で見積もる。 */
export const REPEAT_ROUND_FACTOR = 0.5;

export const DEFAULT_ROUNDS: Readonly<Record<StudyExamSubjectSetting["mode"], number>> = {
  beginner: 3,
  review: 1,
};

/**
 * 本に使う周回数を返す。科目の設定に周回数があればそれ、無ければ本の既定 (ルートの rounds)、それも無ければ
 * 進め方の既定 (初学者3・学び直し1) になる。
 * ※なぜなら周回の要否は本の性質で決まり (網羅系は3周、講義系の参考書と過去問は1周)、科目で一律に掛けると
 * 参考書や過去問まで3周になるためである。
 */
export function bookRounds(b: StudyTextbookRouteBook, setting: StudyExamSubjectSetting): number {
  if (setting.rounds && setting.rounds > 0) return setting.rounds;
  if (typeof b.rounds === "number" && b.rounds > 0) return b.rounds;
  return DEFAULT_ROUNDS[setting.mode];
}

/**
 * 本の期間を周回ごとに分ける。
 *
 * @what: 1周目を1、2周目以降を REPEAT_ROUND_FACTOR (0.5) の重みで日数を配り、周回ごとの開始日と終了日を返す。
 * @why: 見積もり (estimateMinutes) が2周目以降を半分の時間で数えるので、期間も同じ重みで分けると表の
 *   幅が時間に比例する。1周目に長く、復習の周回に短くという実際の進め方にも合う。
 * @failure: 日数が周回数に満たないときは、どの周回も1日は持つように後ろの周回を最後の日に重ねる。
 *   endDate が startDate より前なら1日として扱う。rounds が1未満なら1周として返す。
 */
export function splitPasses(startDate: string, endDate: string, rounds: number): BookPass[] {
  const n = Math.max(1, rounds);
  const days = daysBetween(startDate, endDate) + 1;
  const weights = Array.from({ length: n }, (_, i) => (i === 0 ? 1 : REPEAT_ROUND_FACTOR));
  const total = weights.reduce((a, b) => a + b, 0);
  const out: BookPass[] = [];
  let cum = 0;
  let cursor = 0;
  for (let i = 0; i < n; i++) {
    cum += weights[i]!;
    const endExclusive =
      i === n - 1 ? days : Math.max(cursor + 1, Math.round((cum / total) * days));
    const s0 = Math.min(cursor, days - 1);
    const e0 = Math.max(s0 + 1, Math.min(days, endExclusive));
    out.push({
      round: i + 1,
      startDate: addDays(startDate, s0),
      endDate: addDays(startDate, e0 - 1),
    });
    cursor = e0;
  }
  return out;
}

/** 周回1回ぶんの期間で、本の期間を周回の重み (1周目1、2周目以降0.5) で分けたもの。 */
export interface BookPass {
  /** 1始まりの周回を表す。 */
  round: number;
  startDate: string;
  endDate: string;
}

export interface ScheduledBook {
  book: GridBook;
  /** この本で届く偏差値 (数値のレベルの最大)。無ければ null。 */
  reach: number | null;
  /** 見積もった分で、量が無ければ null になる。 */
  minutes: number | null;
  rounds: number;
  /** 列の中で占める割合 (0〜1)。量が無ければ null になる。 */
  share: number | null;
  /** 起点からの週 (ゼロ始まり)。期間が決まらない / 量が無いときは null になる。 */
  startWeek: number | null;
  endWeek: number | null;
  startDate: string | null;
  endDate: string | null;
  /** 周回ごとの期間で、日付が無ければ空になる。 */
  passes: BookPass[];
}

/** 同じ時間軸の上で順に進める本の列を表す。 */
export interface Lane {
  id: string;
  label: string;
  flow: StudyTextbookRouteFlow;
  books: ScheduledBook[];
  /** 量が分かる本の合計 (分) を持つ。 */
  minutes: number;
  unknownVolume: number;
}

export interface SubjectSchedule {
  subjectId: string;
  subjectLabel: string;
  setting: StudyExamSubjectSetting;
  lanes: Lane[];
  /** 量が分かる本の合計 (分) を持つ。 */
  totalMinutes: number;
  /** 量が分からない本の数を持つ。 */
  unknownVolume: number;
  /** 期間 (週)。決まらなければ null になる。 */
  weeks: number | null;
  startDate: string;
  /**
   * 起点の決まり方を表す。route = ルートの区分の「始める時期」から / exam = 試験日から週の時間で逆算 /
   * setting = 始める日の設定 / today = 今日を表す。日付が決まらないときは null になる。
   */
  startSource: "route" | "exam" | "setting" | "today" | null;
  /** 始める時期の表示名 (例 "小4の春")。startSource が route のときだけ入る。 */
  startLabel: string | null;
  endDate: string | null;
  /** 使う週の分で、設定の値か試験日からの逆算になる。どちらも無ければ null になる。 */
  weeklyMinutes: number | null;
  weeklySource: "setting" | "derived" | null;
  /** 試験日があるとき、試験日までに終えるのに要る週の分を持つ。 */
  requiredWeekly: number | null;
  /** 設定の週の時間では試験日を越えるかを持つ。 */
  overExam: boolean;
  /** 最も重い週の分を持つ。時期で置く (route) と偏るので、平均 (weeklyMinutes) と並べて出す。 */
  peakWeekly: number | null;
}

export function bookFloor(b: StudyTextbookRouteBook): number | null {
  const diff = bookDifficulty(b);
  if (diff !== null) return diff.from;
  let min: number | null = null;
  for (const l of b.levels) {
    if (typeof l.deviation === "number" && l.deviation > 0 && (min === null || l.deviation < min))
      min = l.deviation;
  }
  return min;
}

/** 本1冊の見積もり (分) を返す。量が無ければ null になる。 */
export function estimateMinutes(b: StudyTextbookRouteBook, rounds: number): number | null {
  if (!b.volume || b.volume <= 0) return null;
  const unit = b.volume_unit ?? "問";
  const mpu =
    b.minutes_per_unit && b.minutes_per_unit > 0
      ? b.minutes_per_unit
      : (DEFAULT_MINUTES_PER_UNIT[unit] ?? 10);
  const roundsFactor = 1 + Math.max(0, rounds - 1) * REPEAT_ROUND_FACTOR;
  return Math.round(b.volume * mpu * roundsFactor);
}

/**
 * 科目で使う本を選ぶ。
 *
 * @what: 推奨 (primary) を使い、代替と補助は使わない。上位 (advanced) は、推奨の本で届かない偏差値まで
 *   要るときだけ使い、同じ区分で同じ偏差値に届く上位の本は最初の1冊にする。目標があれば、目標より上で
 *   始まる本は外す。学び直しでいまの偏差値があれば、それより下で終わる本を外す。数値を持たない本は
 *   初学者では残し (講義系の参考書や共テの過去問)、学び直しでは外す。
 *   目標の志望校があれば、他校のための本 (他校の過去問) は外す。設定で外した区分の本は使わない。
 * @why: 上位の本を全部足すと、実戦の問題集が互いに代わりになるものまで積み上がり、小学生に週40時間の
 *   ような負荷が出る (代表の指摘、2026-09-06)。上位は目標に届くための不足分だけにし、過去問は志望校の
 *   ものだけにする。
 * @failure: 条件に合う本が無ければ空を返す (呼び手は「この設定で使う本がありません」と出す)。推奨の本が
 *   1冊も無い区分では上位の本を全部の偏差値で使う (推奨で届く偏差値が無いため)。目標の志望校の名が
 *   ルートの校名と一致しないときは、校名つきの本を全部外す。
 */
export function selectBooks(
  books: readonly GridBook[],
  setting: StudyExamSubjectSetting,
  goalDeviation: number | null,
  goalSchool: string | null = null,
): GridBook[] {
  const current =
    setting.mode === "review" && setting.current_deviation ? setting.current_deviation : null;
  const excluded = new Set(setting.excluded_tracks ?? []);
  const fits = (gb: GridBook, reach: number | null): boolean => {
    // 使わない区分 (設定で外した塾の教材・銀本など) の本は使わない
    if (excluded.has(gb.trackId)) return false;
    // 特定の志望校のための本 (過去問) は、目標の志望校が決まっていればその校のものだけ使う
    if (goalSchool !== null && gb.book.school && gb.book.school !== goalSchool) return false;
    const floor = bookFloor(gb.book);
    if (reach === null) return setting.mode === "beginner";
    if (goalDeviation !== null && floor !== null && floor > goalDeviation) return false;
    if (current !== null && reach < current) return false;
    return true;
  };
  const primaries = books.filter(
    (gb) => gb.book.role === "primary" && fits(gb, bookCeiling(gb.book)),
  );
  // 区分ごとに、推奨の本で届く偏差値の最大を取る
  const primaryMax = new Map<string, number>();
  for (const gb of primaries) {
    const r = bookCeiling(gb.book);
    if (r !== null && r > (primaryMax.get(gb.trackId) ?? -Infinity)) primaryMax.set(gb.trackId, r);
  }
  // 上位の本は、推奨の本で届かない偏差値まで要るときだけ使う。同じ区分で同じ偏差値に届く上位の本は
  // 最初の1冊にする (実戦の問題集は互いに代わりになるものが多い)
  const taken = new Map<string, Set<number>>();
  const advanced = books.filter((gb) => {
    if (gb.book.role !== "advanced") return false;
    const reach = bookCeiling(gb.book);
    if (reach === null || !fits(gb, reach)) return false;
    if (reach <= (primaryMax.get(gb.trackId) ?? -Infinity)) return false;
    const set = taken.get(gb.trackId) ?? new Set<number>();
    if (set.has(reach)) return false;
    set.add(reach);
    taken.set(gb.trackId, set);
    return true;
  });
  // 推奨と上位を別々に選んだので、受け取った並び (区分の順、出典の順) に戻すために元の配列で絞り直す
  const chosen = new Set([...primaries, ...advanced]);
  return books.filter((gb) => chosen.has(gb));
}

/**
 * 並び順を決める。
 *
 * @what: 区分 (切り口) の順を先に守り、同じ区分の中では届く偏差値の低い順、同じなら受け取った順にする。
 *   数値の無い本は区分の中で先頭に置く。
 * @why: ルートの区分は出典が組んだ進み方なので、偏差値だけで並べ直すと「目安の無い実戦の本」が
 *   先頭に来てしまう。区分の順を土台にし、偏差値は区分の中の並びだけに使う。
 */
export function orderBooks(
  books: readonly GridBook[],
  trackOrder: readonly string[] = [],
): GridBook[] {
  const trackIndex = (id: string) => {
    const i = trackOrder.indexOf(id);
    return i === -1 ? trackOrder.length : i;
  };
  return [...books]
    .map((gb, i) => ({ gb, i, reach: bookCeiling(gb.book), track: trackIndex(gb.trackId) }))
    .sort((a, b) => {
      if (a.track !== b.track) return a.track - b.track;
      const ra = a.reach ?? -1;
      const rb = b.reach ?? -1;
      if (ra !== rb) return ra - rb;
      return a.i - b.i;
    })
    .map((x) => x.gb);
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function daysBetween(a: string, b: string): number {
  return Math.round(
    (new Date(`${b}T00:00:00Z`).getTime() - new Date(`${a}T00:00:00Z`).getTime()) / 86400000,
  );
}

/**
 * 選んだ本を列に分ける。
 *
 * @what: 「順番」(in_turn) の区分は区分の順に1本の列へ連ねる (列の名は区分名を「→」でつなぐ)。「並行」
 *   (parallel) の区分はそれぞれ1本の列にする。列の中の本は orderBooks の順に並べる。
 * @why: 基礎固め → 典型問題 → 実戦は順に進めるが、計算や単語帳は期間全体で並行して進める。区分の
 *   進め方 (flow) はルートの出典の組み方から入れてある。
 */
export function buildLanes(
  subject: GridSubject,
  chosen: readonly GridBook[],
): { label: string; flow: StudyTextbookRouteFlow; trackIds: string[]; books: GridBook[] }[] {
  const lanes: {
    label: string;
    flow: StudyTextbookRouteFlow;
    trackIds: string[];
    books: GridBook[];
  }[] = [];
  const inTurn = subject.tracks.filter((t) => t.flow === "in_turn");
  const inTurnBooks = chosen.filter((gb) => inTurn.some((t) => t.id === gb.trackId));
  if (inTurnBooks.length > 0) {
    const used = inTurn.filter((t) => inTurnBooks.some((gb) => gb.trackId === t.id));
    lanes.push({
      label: used.map((t) => t.label).join(" → "),
      flow: "in_turn",
      trackIds: used.map((t) => t.id),
      books: inTurnBooks,
    });
  }
  for (const t of subject.tracks) {
    if (t.flow === "in_turn") continue;
    const books = chosen.filter((gb) => gb.trackId === t.id);
    if (books.length > 0) lanes.push({ label: t.label, flow: "parallel", trackIds: [t.id], books });
  }
  // 区分に属さない本 (ルートの区分が消えたなど) は最後の列に置く
  const covered = new Set(lanes.flatMap((l) => l.books.map((b) => b.key)));
  const rest = chosen.filter((gb) => !covered.has(gb.key));
  if (rest.length > 0) lanes.push({ label: "その他", flow: "parallel", trackIds: [], books: rest });
  return lanes;
}

type RawLane = ReturnType<typeof buildLanes>[number];

function newEntry(gb: GridBook, minutes: number | null, rounds: number): ScheduledBook {
  return {
    book: gb,
    reach: bookCeiling(gb.book),
    minutes,
    rounds,
    share: null,
    startWeek: null,
    endWeek: null,
    startDate: null,
    endDate: null,
    passes: [],
  };
}

function finishLane(l: RawLane, books: ScheduledBook[], laneTotal: number): Lane {
  return {
    id: l.trackIds.join("+") || "rest",
    label: l.label,
    flow: l.flow,
    books,
    minutes: laneTotal,
    unknownVolume: books.filter((b) => b.minutes === null).length,
  };
}

/**
 * どの列も同じ期間 (weeks 週) で終わるように、列の中の本を量に比例して置く。weeks が null なら日付を
 * 付けず、列の中の割合だけを入れる。
 */
function layoutProportional(
  rawLanes: RawLane[],
  laneMinutes: number[],
  roundsOf: (gb: GridBook) => number,
  weeks: number | null,
  startDate: string,
): Lane[] {
  return rawLanes.map((l, li) => {
    const laneTotal = laneMinutes[li] ?? 0;
    let cum = 0;
    const books = l.books.map((gb) => {
      const rounds = roundsOf(gb);
      const minutes = estimateMinutes(gb.book, rounds);
      const entry = newEntry(gb, minutes, rounds);
      if (minutes === null || laneTotal === 0) return entry;
      const from = cum / laneTotal;
      cum += minutes;
      const to = cum / laneTotal;
      entry.share = minutes / laneTotal;
      if (weeks !== null) {
        entry.startWeek = Math.min(weeks - 1, Math.floor(from * weeks));
        entry.endWeek = Math.max(entry.startWeek, Math.min(weeks - 1, Math.ceil(to * weeks) - 1));
        entry.startDate = addDays(startDate, entry.startWeek * 7);
        entry.endDate = addDays(startDate, entry.endWeek * 7 + 6);
        entry.passes = splitPasses(entry.startDate, entry.endDate, rounds);
      }
      return entry;
    });
    return finishLane(l, books, laneTotal);
  });
}

/**
 * 試験日の月から months か月前の月の初日を返す。
 *
 * @failure: examDate は YYYY-MM-DD を前提にし、それ以外の文字列では NaN 入りの日付を返す (呼び手は
 *   検証済みの試験日だけを渡す)。months が負なら後ろの月になる (列の計算で翌月を出すのに使う)。
 *   剰余は負の値でも1〜12に収まるように正の剰余にしてある。
 */
export function monthsBeforeExam(examDate: string, months: number): string {
  const y = Number(examDate.slice(0, 4));
  const m = Number(examDate.slice(5, 7));
  const total = y * 12 + (m - 1) - months;
  const y2 = Math.floor(total / 12);
  const m2 = (((total % 12) + 12) % 12) + 1;
  return `${y2}-${String(m2).padStart(2, "0")}-01`;
}

interface LaneWindow {
  start: string;
  /** 終わりは含まない (次の区分の初日か試験日)。 */
  end: string;
  books: GridBook[];
}

/**
 * 列の本を、区分の「始める時期」(試験日の何か月前) で決まる期間 (window) に分ける。
 *
 * @what: 順番の列は区分ごとに [その区分の初日, 次の区分の初日) を期間にし、始める時期が同じ区分は1つの
 *   期間にまとめる。後ろの区分が前より早く始まる指定は前に合わせる (順番の列は時間の順に進むため)。
 *   最後の期間と並行の列は試験日で終わる。
 * @why: 「基礎固めは小4の春から、実戦は小6の春から」という組み方をそのまま時間軸に置くためである。
 * @failure: 時期の無い区分は前の区分の時期を引き継ぎ、先頭なら試験日そのものになる。期間の長さが1日に
 *   満たない (時期が試験日と同じ月など) ときは1日にして、本の日付が逆転しないようにする。
 */
function laneWindows(l: RawLane, tracks: GridTrack[], examDate: string): LaneWindow[] {
  const byId = new Map(tracks.map((t) => [t.id, t] as const));
  const groups: { months: number; books: GridBook[] }[] = [];
  let prev: number | null = null;
  for (const tid of l.trackIds) {
    const t = byId.get(tid);
    let months: number = t?.startMonthsBeforeExam ?? prev ?? 0;
    if (prev !== null && months > prev) months = prev;
    prev = months;
    const books = l.books.filter((gb) => gb.trackId === tid);
    const last = groups[groups.length - 1];
    if (last && last.months === months) last.books.push(...books);
    else groups.push({ months, books });
  }
  const orphan = l.books.filter((gb) => !l.trackIds.includes(gb.trackId));
  if (orphan.length > 0) {
    const last = groups[groups.length - 1];
    if (last) last.books.push(...orphan);
    else groups.push({ months: 0, books: orphan });
  }
  return groups.map((g, i) => {
    const start = g.months > 0 ? monthsBeforeExam(examDate, g.months) : examDate;
    const next = groups[i + 1];
    let end = next && next.months > 0 ? monthsBeforeExam(examDate, next.months) : examDate;
    if (daysBetween(start, end) < 1) end = addDays(start, 1);
    return { start, end, books: g.books };
  });
}

/** 列ごとに、期間の中の本を量に比例して日付に置く。週は subjectStart からの通し番号になる。 */
function layoutByWindows(
  rawLanes: RawLane[],
  laneMinutes: number[],
  roundsOf: (gb: GridBook) => number,
  windows: LaneWindow[][],
  subjectStart: string,
  weeks: number,
): Lane[] {
  return rawLanes.map((l, li) => {
    const laneTotal = laneMinutes[li] ?? 0;
    const books: ScheduledBook[] = [];
    for (const w of windows[li] ?? []) {
      const days = daysBetween(w.start, w.end);
      const winTotal = w.books.reduce(
        (acc, gb) => acc + (estimateMinutes(gb.book, roundsOf(gb)) ?? 0),
        0,
      );
      let cum = 0;
      for (const gb of w.books) {
        const rounds = roundsOf(gb);
        const minutes = estimateMinutes(gb.book, rounds);
        const entry = newEntry(gb, minutes, rounds);
        books.push(entry);
        if (minutes === null || laneTotal === 0) continue;
        entry.share = minutes / laneTotal;
        const from = winTotal > 0 ? cum / winTotal : 0;
        cum += minutes;
        const to = winTotal > 0 ? cum / winTotal : 1;
        const s0 = Math.min(days - 1, Math.floor(from * days));
        const e0 = Math.max(s0 + 1, Math.min(days, Math.ceil(to * days)));
        entry.startDate = addDays(w.start, s0);
        entry.endDate = addDays(w.start, e0 - 1);
        entry.startWeek = Math.min(
          weeks - 1,
          Math.floor(daysBetween(subjectStart, entry.startDate) / 7),
        );
        entry.endWeek = Math.min(
          weeks - 1,
          Math.floor(daysBetween(subjectStart, entry.endDate) / 7),
        );
        entry.passes = splitPasses(entry.startDate, entry.endDate, rounds);
      }
    }
    return finishLane(l, books, laneTotal);
  });
}

/**
 * 週ごとの負荷の最大 (分) を返す。本の分を期間の日に均等に配り、起点からの7日ごとに足す。日付の無い本は
 * 数えない。※なぜなら週の番号で配ると、週の境目で前後の本が同じ週に重なって見かけの山ができるためである。
 *
 * @failure: 日付の無い本 (量が未登録) は負荷に入らないので、実際より軽く出ることがある。本が1冊も無ければ
 *   ゼロを返す。起点より前の日付は第1週に、期間の後ろに出た日付は最後の週に寄せる。
 */
export function peakWeeklyLoad(lanes: Lane[], subjectStart: string, weeks: number): number {
  const load = new Array<number>(Math.max(1, weeks)).fill(0);
  for (const l of lanes) {
    for (const b of l.books) {
      if (b.minutes === null || b.startDate === null || b.endDate === null) continue;
      const d0 = daysBetween(subjectStart, b.startDate);
      const days = daysBetween(b.startDate, b.endDate) + 1;
      const perDay = b.minutes / Math.max(1, days);
      for (let d = d0; d < d0 + days; d++) {
        const w = Math.min(weeks - 1, Math.max(0, Math.floor(d / 7)));
        load[w] = (load[w] ?? 0) + perDay;
      }
    }
  }
  return Math.ceil(Math.max(...load));
}

/**
 * 1科目のスケジュールを組む。
 *
 * @what: 初学者は起点を入力させない。試験日 (期限) があり、使う区分に「始める時期」(試験日の何か月前) が
 *   あればその時期に区分を置き、いつから始めるかを返す (startSource = route)。時期の無いルートでは、
 *   週の時間があれば試験日から逆算して始める日を出す (exam)。週の時間が無ければ今日から試験日までを
 *   期間にし、終えるのに必要な週の分を返す (today)。試験日も無ければ日付を付けない。
 *   学び直しは始める日 (無ければ今日) を起点にし、週の時間があれば 合計 ÷ 週の時間、無ければ試験日までの
 *   週数を期間にする。どちらも無ければ日付を付けず、列の中の割合だけを返す。
 * @why: 「いつから始めればいいか」はルートの組み方が決めるもので、小4から使う本を小1から始めることは
 *   ない (代表の指摘、2026-09-05)。学び直しはいま始めるので起点を入れる。期限だけを入れた初学者でも
 *   期間が決まるようにするのは、区分の時期を持たないカリキュラム (教養・技能) で時系列の表が出ない
 *   ためである (代表の指摘、2026-09-13)。
 */
export function scheduleSubject(
  subject: GridSubject,
  setting: StudyExamSubjectSetting,
  goalDeviation: number | null,
  planStartDate: string | null,
  today: string,
  examDate: string | null,
  goalSchool: string | null = null,
): SubjectSchedule {
  const roundsOf = (gb: GridBook) => bookRounds(gb.book, setting);
  const settingWeekly =
    setting.weekly_minutes && setting.weekly_minutes > 0 ? setting.weekly_minutes : null;
  const chosen = orderBooks(
    selectBooks(subject.books, setting, goalDeviation, goalSchool),
    subject.tracks.map((t) => t.id),
  );
  const rawLanes = buildLanes(subject, chosen);

  let totalMinutes = 0;
  let unknownVolume = 0;
  const laneMinutes = rawLanes.map((l) => {
    let m = 0;
    for (const gb of l.books) {
      const est = estimateMinutes(gb.book, roundsOf(gb));
      if (est === null) unknownVolume += 1;
      else m += est;
    }
    totalMinutes += m;
    return m;
  });

  const base = {
    subjectId: subject.id,
    subjectLabel: subject.label,
    setting,
    totalMinutes,
    unknownVolume,
    requiredWeekly: null as number | null,
    overExam: false,
    startLabel: null as string | null,
    peakWeekly: null as number | null,
  };

  if (setting.mode === "beginner") {
    const usedTrackIds = rawLanes.flatMap((l) => l.trackIds);
    const usedTracks = subject.tracks.filter((t) => usedTrackIds.includes(t.id));
    const hasTiming =
      examDate !== null &&
      usedTracks.length > 0 &&
      usedTracks.every((t) => t.startMonthsBeforeExam !== null);
    if (hasTiming) {
      const windows = rawLanes.map((l) => laneWindows(l, subject.tracks, examDate));
      const startDate = windows
        .map((ws) => ws[0]?.start ?? examDate)
        .reduce((a, b) => (a < b ? a : b));
      const weeks = Math.max(1, Math.ceil(daysBetween(startDate, examDate) / 7));
      const lanes = layoutByWindows(rawLanes, laneMinutes, roundsOf, windows, startDate, weeks);
      const first = usedTracks.reduce((a, b) =>
        (b.startMonthsBeforeExam ?? 0) > (a.startMonthsBeforeExam ?? 0) ? b : a,
      );
      const weekly = totalMinutes > 0 ? Math.ceil(totalMinutes / weeks) : null;
      return {
        ...base,
        lanes,
        weeks,
        startDate,
        startSource: "route",
        startLabel: first.startLabel,
        endDate: addDays(examDate, -1),
        weeklyMinutes: weekly,
        weeklySource: weekly === null ? null : "derived",
        requiredWeekly: weekly,
        peakWeekly: totalMinutes > 0 ? peakWeeklyLoad(lanes, startDate, weeks) : null,
      };
    }
    if (examDate !== null && settingWeekly !== null && totalMinutes > 0) {
      const weeks = Math.max(1, Math.ceil(totalMinutes / settingWeekly));
      const startDate = addDays(examDate, -weeks * 7);
      return {
        ...base,
        lanes: layoutProportional(rawLanes, laneMinutes, roundsOf, weeks, startDate),
        weeks,
        startDate,
        startSource: "exam",
        endDate: addDays(examDate, -1),
        weeklyMinutes: settingWeekly,
        weeklySource: "setting",
        peakWeekly: settingWeekly,
      };
    }
    // 週の時間が無くても期限があれば、今日から期限までに割り付け、終えるのに必要な週の分を返す。
    // ※なぜなら期限を入れた人は「そこまでに終える」ことを決めており、週に使える時間はその結果として
    // 知りたい値だからである (代表の指摘、2026-09-13)。
    const weeksUntil =
      examDate !== null && daysBetween(today, examDate) > 0
        ? Math.max(1, Math.floor(daysBetween(today, examDate) / 7))
        : null;
    if (weeksUntil !== null && totalMinutes > 0) {
      const weekly = Math.ceil(totalMinutes / weeksUntil);
      return {
        ...base,
        lanes: layoutProportional(rawLanes, laneMinutes, roundsOf, weeksUntil, today),
        weeks: weeksUntil,
        startDate: today,
        startSource: "today",
        endDate: addDays(today, weeksUntil * 7 - 1),
        weeklyMinutes: weekly,
        weeklySource: "derived",
        requiredWeekly: weekly,
        peakWeekly: weekly,
      };
    }
    return {
      ...base,
      lanes: layoutProportional(rawLanes, laneMinutes, roundsOf, null, today),
      weeks: null,
      startDate: today,
      startSource: null,
      endDate: null,
      weeklyMinutes: null,
      weeklySource: null,
    };
  }

  // 学び直しは始める日 (無ければ今日) を起点にする
  const startDate = planStartDate ?? today;
  const weeksUntilExam =
    examDate !== null && daysBetween(startDate, examDate) > 0
      ? Math.max(1, Math.floor(daysBetween(startDate, examDate) / 7))
      : null;
  const requiredWeekly =
    weeksUntilExam !== null && totalMinutes > 0 ? Math.ceil(totalMinutes / weeksUntilExam) : null;
  let weeks: number | null = null;
  let weeklyMinutes: number | null = null;
  let weeklySource: SubjectSchedule["weeklySource"] = null;
  if (settingWeekly !== null && totalMinutes > 0) {
    weeks = Math.max(1, Math.ceil(totalMinutes / settingWeekly));
    weeklyMinutes = settingWeekly;
    weeklySource = "setting";
  } else if (weeksUntilExam !== null && totalMinutes > 0) {
    weeks = weeksUntilExam;
    weeklyMinutes = requiredWeekly;
    weeklySource = "derived";
  }
  const endDate = weeks !== null ? addDays(startDate, weeks * 7 - 1) : null;
  return {
    ...base,
    lanes: layoutProportional(rawLanes, laneMinutes, roundsOf, weeks, startDate),
    weeks,
    startDate,
    startSource: planStartDate !== null ? "setting" : "today",
    endDate,
    weeklyMinutes,
    weeklySource,
    requiredWeekly,
    overExam: examDate !== null && endDate !== null && endDate > examDate,
    peakWeekly: weeklyMinutes,
  };
}

/** 受験科目 (enabled) だけを対象に、科目ごとのスケジュールを組む。 */
export function buildSchedule(
  merged: MergedTarget,
  plan: Pick<StudyExamPlan, "goal_deviation" | "exam_date" | "start_date" | "subjects"> & {
    goal_school?: string;
  },
  today: string,
): SubjectSchedule[] {
  const startDate = plan.start_date && plan.start_date.length > 0 ? plan.start_date : null;
  const examDate = plan.exam_date && plan.exam_date.length > 0 ? plan.exam_date : null;
  const goal = plan.goal_deviation && plan.goal_deviation > 0 ? plan.goal_deviation : null;
  const goalSchool = plan.goal_school && plan.goal_school.length > 0 ? plan.goal_school : null;
  const out: SubjectSchedule[] = [];
  for (const s of merged.subjects) {
    const setting = plan.subjects[s.id];
    if (!setting || !setting.enabled) continue;
    out.push(scheduleSubject(s, setting, goal, startDate, today, examDate, goalSchool));
  }
  return out;
}

/** 分を「n時間m分」で出す。 */
export function formatMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}分`;
  if (m === 0) return `${h}時間`;
  return `${h}時間${m}分`;
}

/* ------------------------------------------------------------------ */
/* 時系列の表                                                          */
/* ------------------------------------------------------------------ */

/** 時系列の表の1列 (週か月) で、startDate〜endDate は閉区間になる。 */
export interface TimelineColumn {
  key: string;
  label: string;
  startDate: string;
  endDate: string;
  /** 見出しに添える学年 ("小4")。最初の列と年度の始まり (4月) の列にだけ入り、他は null になる。 */
  grade: string | null;
}

/** 表に置く1つの周回 (周回が1回の本はその本そのもの)。round は周回が2回以上のときだけ入る。 */
export interface TimelineItem {
  book: ScheduledBook;
  round: number | null;
  startDate: string;
  endDate: string;
}

/** 列の中で本 (の周回) が占める区間を表す。同じ区間に2つ以上が入ることもある (短い周回が同じ月に収まるとき)。 */
export interface TimelineSegment {
  from: number;
  to: number;
  items: TimelineItem[];
}

export interface TimelineRow {
  subjectId: string;
  subjectLabel: string;
  /** 科目の最初の行だけ true になる (科目名のセルを rowspan で出すため)。 */
  firstOfSubject: boolean;
  laneCount: number;
  lane: Lane;
  /** 表の列の並びに沿った区間を持つ。空きは含めず、描く側が詰める。 */
  segments: TimelineSegment[];
  /** 日付が決まらない本 (量が未登録など)。 */
  undated: ScheduledBook[];
}

export interface ScheduleTimeline {
  /** 列の刻み。26週までは週、それより長ければ月になる。 */
  granularity: "week" | "month";
  columns: TimelineColumn[];
  rows: TimelineRow[];
  /** 試験日が入る列の位置で、期間の外なら null になる。 */
  examColumn: number | null;
  /** 全科目の中で最も長い期間 (週)。どの科目にも期間が無ければ0になる。 */
  weeks: number;
}

/** 週の列は26週までにし、それより長い期間は月の列にする。 */
export const TIMELINE_WEEK_COLUMN_MAX = 26;

/**
 * 起点から終わりの日までの列を作る。
 *
 * @what: 26週までは週ごと (見出しは週の初日の M/D)、それより長ければ月ごと (見出しは M月、
 *   最初と1月は年つき) の列にする。月の列は暦の月で切り、最初と最後の列は期間の端で切る。
 * @why: 2か月の計画は週で、1年の計画は月で追う方が読みやすい。列を暦の日付で持つと、起点の違う
 *   科目をずらして置くときに週の丸めが要らない。
 */
export function timelineColumns(startDate: string, endDate: string): TimelineColumn[] {
  if (!startDate || endDate < startDate) return [];
  const weeks = Math.ceil((daysBetween(startDate, endDate) + 1) / 7);
  if (weeks <= TIMELINE_WEEK_COLUMN_MAX) {
    const out: TimelineColumn[] = [];
    for (let w = 0; w < weeks; w++) {
      const d = addDays(startDate, w * 7);
      out.push({
        key: d,
        label: `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`,
        startDate: d,
        endDate: addDays(d, 6),
        grade: null,
      });
    }
    return out;
  }
  const out: TimelineColumn[] = [];
  let cur = startDate;
  while (cur <= endDate) {
    const ym = cur.slice(0, 7);
    const month = Number(ym.slice(5, 7));
    const nextMonth = monthsBeforeExam(`${ym}-01`, -1);
    const last = addDays(nextMonth, -1);
    const label = out.length === 0 || month === 1 ? `${ym.slice(0, 4)}年${month}月` : `${month}月`;
    out.push({
      key: ym,
      label,
      startDate: cur,
      endDate: last < endDate ? last : endDate,
      grade: null,
    });
    cur = nextMonth;
  }
  return out;
}

function columnOfDate(columns: TimelineColumn[], date: string): number {
  for (let i = 0; i < columns.length; i++) {
    const c = columns[i]!;
    if (date >= c.startDate && date <= c.endDate) return i;
  }
  return date < (columns[0]?.startDate ?? "") ? 0 : columns.length - 1;
}

/**
 * 列 (lane) の本を表の区間に割り付ける。
 *
 * @what: 本の周回ごとの期間 (passes) を列の位置に写す。周回が1回の本は本そのものを置く。前と同じ列で
 *   終わるもの (短くて次の列に届かない周回) は前の区間に同居させ、区間どうしは重ならないようにする。
 * @why: 表は colspan で描くので、区間は左から順に重ならず並ぶ必要がある。周回を分けて置くと
 *   「2周目はいつか」が表で分かる (代表の要望、2026-09-06)。
 */
export function laneSegments(lane: Lane, columns: TimelineColumn[]): TimelineSegment[] {
  const out: TimelineSegment[] = [];
  let nextFree = 0;
  for (const b of lane.books) {
    if (b.startDate === null || b.endDate === null || columns.length === 0) continue;
    const items: TimelineItem[] =
      b.passes.length > 1
        ? b.passes.map((p) => ({
            book: b,
            round: p.round,
            startDate: p.startDate,
            endDate: p.endDate,
          }))
        : [{ book: b, round: null, startDate: b.startDate, endDate: b.endDate }];
    for (const it of items) {
      const from = columnOfDate(columns, it.startDate);
      const to = columnOfDate(columns, it.endDate);
      const start = Math.max(from, nextFree);
      const last = out[out.length - 1];
      if (start > to && last) {
        last.items.push(it);
        continue;
      }
      out.push({ from: start, to: Math.max(start, to), items: [it] });
      nextFree = Math.max(start, to) + 1;
    }
  }
  return out;
}

/**
 * 科目ごとのスケジュールを1枚の時系列の表にまとめる。
 *
 * @what: 全科目の中で最も早い起点から最も遅い終わりまでの列を作り、科目 → 列 (lane) の順に行を並べる。
 *   本は自分の日付で列に写るので、起点の違う科目もそのまま置ける。期間が決まらない科目の行は区間を
 *   持たず、本はすべて undated に入る。
 *   学年の関数 (gradeOf、学習者の生年月日から) があれば、最初の列と4月の列に学年を添える。
 * @why: 科目をまたいで同じ時間軸に並べると、いつ何が重なるかが一目で分かる (代表の要望、2026-09-05)。
 */
export function buildTimeline(
  schedule: SubjectSchedule[],
  examDate: string | null,
  gradeOf: ((date: string) => string | null) | null = null,
): ScheduleTimeline {
  const dated = schedule.filter((s) => s.weeks !== null && s.endDate !== null);
  const startDate =
    dated.length > 0 ? dated.map((s) => s.startDate).reduce((a, b) => (a < b ? a : b)) : "";
  const endDate =
    dated.length > 0 ? dated.map((s) => s.endDate ?? "").reduce((a, b) => (a > b ? a : b)) : "";
  const weeks = startDate ? Math.max(1, Math.ceil((daysBetween(startDate, endDate) + 1) / 7)) : 0;
  const columns = startDate ? timelineColumns(startDate, endDate) : [];
  // 学年は最初の列と、年度の始まり (4月1日を含む列) に添える
  if (gradeOf !== null) {
    columns.forEach((c, i) => {
      const april = `${c.startDate.slice(0, 4)}-04-01`;
      if (i === 0 || (april >= c.startDate && april <= c.endDate))
        c.grade = gradeOf(c.startDate < april && i !== 0 ? april : c.startDate);
    });
  }
  const rows: TimelineRow[] = [];
  for (const sub of schedule) {
    sub.lanes.forEach((lane, i) => {
      rows.push({
        subjectId: sub.subjectId,
        subjectLabel: sub.subjectLabel,
        firstOfSubject: i === 0,
        laneCount: sub.lanes.length,
        lane,
        segments: sub.weeks !== null ? laneSegments(lane, columns) : [],
        undated: lane.books.filter((b) => b.startDate === null),
      });
    });
  }
  let examColumn: number | null = null;
  if (examDate !== null && columns.length > 0) {
    // 逆算した期間は試験日の前日で終わるので、試験日そのものは最後の列に含める
    const lastEnd = columns[columns.length - 1]!.endDate;
    if (examDate >= startDate && examDate <= addDays(lastEnd, 1))
      examColumn = columnOfDate(columns, examDate < lastEnd ? examDate : lastEnd);
  }
  return {
    granularity: weeks > TIMELINE_WEEK_COLUMN_MAX ? "month" : "week",
    columns,
    rows,
    examColumn,
    weeks,
  };
}

/** 時系列の表を分ける期間 (低学年 / 高学年) で、from〜to は元の表の列の位置 (閉区間) になる。 */
export interface TimelinePhase {
  key: "lower" | "upper";
  label: string;
  from: number;
  to: number;
}

/** 小1〜小3 (と就学前) を低学年、小4以上を高学年とみなす。 */
function isLowerGrade(grade: string | null): boolean {
  if (grade === null) return true;
  const m = /^小([1-6])$/.exec(grade);
  return m !== null && Number(m[1]) <= 3;
}

/**
 * 時系列の表を「低学年 (小1〜小3)」と「高学年 (小4〜)」に分ける位置を返す。
 *
 * @what: 列の学年 (最初の列と4月の列にだけ付いている) を後ろの列へ引き継ぎ、小4以上の学年が最初に
 *   現れる列で分ける。どちらか一方しか無ければ空を返す (分ける必要が無い)。
 * @why: 低学年の区分を持つルート (中学入試) は小1から入試までの6年分が月の列で並び、108列を1枚で
 *   読めない (代表の判断、2026-09-16)。おとなの学び直し (学年が付かない) や高学年だけのルートでは
 *   切り替えを出さない。
 * @failure: 列が無ければ空を返す。
 */
export function timelinePhases(timeline: ScheduleTimeline): TimelinePhase[] {
  const cols = timeline.columns;
  if (cols.length === 0) return [];
  let grade: string | null = null;
  let firstUpper = -1;
  for (let i = 0; i < cols.length; i++) {
    if (cols[i]!.grade !== null) grade = cols[i]!.grade;
    if (!isLowerGrade(grade)) {
      firstUpper = i;
      break;
    }
  }
  if (firstUpper <= 0) return [];
  return [
    { key: "lower", label: "低学年（小1〜小3）", from: 0, to: firstUpper - 1 },
    { key: "upper", label: "高学年（小4〜）", from: firstUpper, to: cols.length - 1 },
  ];
}

/**
 * 時系列の表から、列の範囲 (from〜to) だけを切り出す。
 *
 * @what: 列を切り、各行の区間を範囲に収まるように詰める。範囲の外だけの区間と、範囲の外だけの周回は
 *   落とす。試験日の列は範囲の中にあるときだけ位置を付け直す。
 * @why: 期間の切り替えで表を分けるとき、スケジュールの計算は変えず、表示だけを切る。
 * @failure: 範囲が列の外なら列の無い表を返す (表示する側は「期間が決まらない」の案内を出す)。
 */
export function sliceTimeline(
  timeline: ScheduleTimeline,
  from: number,
  to: number,
): ScheduleTimeline {
  const columns = timeline.columns.slice(Math.max(0, from), to + 1);
  if (columns.length === 0) {
    return { ...timeline, columns: [], rows: [], examColumn: null, weeks: 0 };
  }
  // 月の列は最初の列にだけ年が付くので、切り出した先頭の列にも年を付ける
  const head = columns[0]!;
  if (timeline.granularity === "month" && !head.label.includes("年")) {
    columns[0] = { ...head, label: `${head.startDate.slice(0, 4)}年${head.label}` };
  }
  const first = columns[0]!.startDate;
  const last = columns[columns.length - 1]!.endDate;
  const rows: TimelineRow[] = timeline.rows.map((r) => ({
    ...r,
    segments: r.segments.flatMap((seg) => {
      if (seg.to < from || seg.from > to) return [];
      const items = seg.items.filter((it) => it.endDate >= first && it.startDate <= last);
      if (items.length === 0) return [];
      return [{ from: Math.max(seg.from, from) - from, to: Math.min(seg.to, to) - from, items }];
    }),
  }));
  const examColumn =
    timeline.examColumn !== null && timeline.examColumn >= from && timeline.examColumn <= to
      ? timeline.examColumn - from
      : null;
  return {
    ...timeline,
    columns,
    rows,
    examColumn,
    weeks: Math.max(1, Math.ceil((daysBetween(first, last) + 1) / 7)),
  };
}
